/**********************************************************************
 -MODULE IMPORTS (ANGULAR BUILT IN AND PACKAGE.JSON MODULES)
 ***********************************************************************/
import { AgmCoreModule } from "@agm/core";
import { AgmJsMarkerClustererModule } from "@agm/js-marker-clusterer";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from "@angular/common/http";
import { APP_INITIALIZER, Injector, NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatListModule } from "@angular/material/list";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { BrowserModule, Title } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { RouterModule } from "@angular/router";
import { LoadingBarHttpClientModule } from "@ngx-loading-bar/http-client";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { NgxDatatableModule } from "@swimlane/ngx-datatable";
import { FormioModule } from "angular-formio/dist";
import { ResizableModule } from "angular-resizable-element";
import { AngularResizedEventModule } from "angular-resize-event";
import { NgSelectModule } from "@ng-select/ng-select";
import { UiSwitchModule } from "ngx-ui-switch";
import { CustomFormsModule } from "ngx-custom-validators";
import { AlertModule } from "ngx-bootstrap/alert";
import { BsDropdownModule } from "ngx-bootstrap/dropdown";
import { CollapseModule } from "ngx-bootstrap/collapse";
import { BsDatepickerModule, DatepickerModule } from "ngx-bootstrap/datepicker";
import { ModalModule } from "ngx-bootstrap/modal";
import { TabsModule } from "ngx-bootstrap/tabs";
import { TooltipModule } from "ngx-bootstrap/tooltip";
import { ButtonsModule } from "ngx-bootstrap/buttons";
import { TagInputModule } from "ngx-chips";
import { DndModule } from "ngx-drag-drop";
import { FilterPipeModule } from "ngx-filter-pipe";
import { ToastrModule } from "ngx-toastr";
import { NgxUploaderModule } from "ngx-uploader";
import { KeycloakAngularModule, KeycloakService } from "keycloak-angular";
import { PopoverModule } from "ngx-bootstrap/popover";
import { SafeHtmlPipe } from "./components/pipes/safe-html-pipe";
import { LabelFilterPipe } from "./components/pipes/label-filter-pipe";
import { ServiceFilterPipe } from "./components/pipes/service-filter-pipe";
import { ReplaceUnderscorePipe } from "./components/pipes/replace-underscore-pipe";
/**********************************************************************
 -GLOBAL AND REUSABLE COMPONENTS
 **********************************************************************/
import { AppComponent } from "./app.component";
import { CancelaryTextButtonComponent } from "./components/cancelary-text-button/cancelary-text-button.component";
import { CollapsiblePaneComponent } from "./components/collapsible-pane/collapsible-pane.component";
import {
  DeterminantBuilderNewComponent,
} from "./components/determinant-builder-section/determinant-builder-new.component";
import {
  DeterminantBuilderSectionComponent,
} from "./components/determinant-builder-section/determinant-builder-section.component";
import { DeterminantBuilderComponent } from "./components/determinant-builder/determinant-builder.component";
import { DocumentEditComponent } from "./components/document/document-edit.component";
import {
  DraggableListGroupItemsComponent,
} from "./components/draggable-list-group-items/draggable-list-group-items.component";
import { HeaderComponent } from "./components/header/header.component";
import { EntityNotFoundComponent } from "./components/entity-not-found/entity-not-found.component";
import { PageNotFoundComponent } from "./components/entity-not-found/page-not-found.component";
import { IconComponent } from "./components/icon/icon.component";
import { LayerHeaderComponent } from "./components/layer-header/layer-header.component";
import { ListGridComponent } from "./components/list-grid/list-grid.component";
import { ListGroupItemComponent } from "./components/list-group-item/list-group-item.component";
import { OwnershipSelectComponent } from "./components/ownership-select/ownership-select.component";
import { DocumentResultEditComponent } from "./components/document-result/document-result-edit.component";
import { TutorialLayerComponent } from "./components/tutorial-layer/tutorial-layer.component";
import { CKEditorModule } from "@ckeditor/ckeditor5-angular";
import { ImportUploaderComponent } from "./components/utils/import-uploader/import-uploader.component";
import { FormBuilderActionsComponent } from "./components/utils/form-builder-actions/form-builder-actions.component";
import { FormBugsDrawerComponent } from "./components/utils/form-bugs-drawer/form-bugs-drawer.component";
import { SourceBadgeComponent } from "./components/source-badge/source-badge.component";

/**********************************************************************
 -Application file components
 ***********************************************************************/
// Guide
import { ApplicationFileFormsContainer } from "./components/pages/application-file/forms-container";
import { ApplicationFileTabsComponent } from "./components/pages/application-file/components/application-file-tabs";
import {
  FormDescriptionComponent,
} from "./components/pages/application-file/components/form-description/form-description.component";
import {
  DeterminantFormComponent,
} from "./components/pages/application-file/components/determinant-form/determinant-form.component";
import { TabMenu } from "./components/tab-menu/tab-menu.component";
/**********************************************************************
 -Preview page components
 ***********************************************************************/
import { PostMessageModule } from "@tekool/ngx-post-message-angular-9/ngx-post-message";
import { FileUploaderComponent } from "./components/file-list-group-item/file-uploader.component";
import { InfixFormulaBuilderComponent } from "./components/formula-builder/formula-builder.component";
import {
  FieldSetAbbreviationComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-abbreviation.component";
import {
  FieldSetAddAnotherPositionComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-add-another-position.component";
import {
  FieldSetCalculatePercentageComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-calculate-percentage.component";
import {
  FieldSetCalculateSharesValueComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-calculate-shares-value.component";
import {
  FieldSetCalculatedDefaultComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-calculated-default.component";
import {
  FieldSetCalculatedMaxComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-calculated-max.component";
import {
  FieldSetClassificationComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-classification.component";
import {
  FieldSetContentComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-content.component";
import {
  FieldSetDateFormatComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-date-format.component";
import {
  FieldSetDateLaterThanNowComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-date-later-than-now.component";
import {
  FieldSetDateComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-date.component";
import {
  FieldSetDefaultComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-default.component";
import {
  FieldSetDescriptionComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-description.component";
import {
  FieldSetDisabledComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-disabled.component";
import {
  FieldSetEarlierThanComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-earlier-than.component";
import {
  FieldSetFieldtypeComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-fieldtype.component";
import {
  FieldSetGridOrderNrComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-grid-ordernr.component";
import {
  FieldSetGuideClassificationComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-guide-classification.component";
import {
  FieldSetHiddenComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-hidden.component";
import {
  FieldSetKeyComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-key.component";
import {
  FieldSetLabelComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-label.component";
import {
  FieldSetLaterThanComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-later-than.component";
import {
  FieldSetMaskComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-mask.component";
import {
  FieldSetMaxActivitiesComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-max-activities.component";
import {
  FieldSetMaxLengthComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-max-length.component";
import {
  FieldSetMaxComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-max.component";
import {
  FieldSetMinActivitiesComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-min-activities.component";
import {
  FieldSetMinLengthComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-min-length.component";
import {
  FieldSetMinRowsComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-min-rows.component";
import {
  FieldSetMinComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-min.component";
import {
  FieldSetMultipleComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-multiple.component";
import {
  FieldSetMyContentComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-mycontent.component";
import {
  FieldSetNotVisibleEditModeComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-notvisible-editmode.component";
import {
  FieldSetNotVisibleComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-notvisible.component";
import {
  FieldSetOlderThanAgeComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-older-than-age.component";
import {
  FieldSetQuestiontypeComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-questiontype.component";
import {
  FieldSetRadioComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-radio.component";
import {
  FieldSetRequiredOneTrueComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-required-one-true.component";
import {
  FieldSetRequiredRowsComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-required-rows.component";
import {
  FieldSetRequiredTotalPercentageComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-required-total-percentage.component";
import {
  FieldSetRequiredComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-required.component";
import {
  FieldSetSelectComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-select.component";
import {
  FieldSetShortLabelComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-short-label.component";
import {
  FieldSetStyleComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-style.component";
import {
  FieldSetTableComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-table.component";
import {
  FieldSetTagComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-tag.component";
import {
  FieldSetTimeComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-time.component";
import {
  FieldSetTooltipComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-tooltip.component";
import {
  FieldSetTotalVisibleInFooterComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-total-visible-in-footer.component";
import {
  FieldOptionsComponent,
} from "./components/pages/application-file/components/form/field-options/field-options.component";
import {
  DescriptionNotificationComponent,
} from "./components/pages/application-file/components/notification/description-notification.component";
import {
  NotificationComponent,
} from "./components/pages/application-file/components/notification/notification.component";
import { SendFileComponent } from "./components/pages/application-file/components/send-file/sendfile.component";
import { CardPageComponent } from "./components/pages/card/card.component";
import { ActivityConditionTypeComponent } from "./components/pages/classifications/activity-condition-type.component";
import {
  ClassificationRegistrationComponent,
} from "./components/pages/classifications/classification-registration.component";
import { ClassificationComponent } from "./components/pages/classifications/classification.component";
import {
  ClassificationTypeNewModalComponent,
} from "./components/pages/classifications/modal/classification-type-new.component";
import { OtherClassificationComponent } from "./components/pages/classifications/other-classification.component";
import { CostRegistrationsComponent } from "./components/pages/crossed-tables/cost-registrations.component";
import { DeterminantRegistrationsComponent } from "./components/pages/crossed-tables/determinant-registrations.component";
/**********************************************************************
 -Tables page components
 ***********************************************************************/
import { DocumentRegistrationsComponent } from "./components/pages/crossed-tables/document-registrations.component";
import { FieldPropertiesComponent } from "./components/pages/crossed-tables/field-properties.component";
import { FormFieldRegistrationsComponent } from "./components/pages/crossed-tables/formfield-registrations.component";
import { InstitutionRegistrationsComponent } from "./components/pages/crossed-tables/institution-registrations.component";
import { DocumentRegistrationModal } from "./components/pages/crossed-tables/modal/document-registration-modal.component";
import { RoleRegistrationsComponent } from "./components/pages/crossed-tables/role-registrations.component";
import { LanguageComponent } from "./components/pages/languages/language.component";
import { LoginComponent } from "./components/pages/login/login.component";
import { PdfExportComponent } from "./components/pages/pdf-export/pdf-export.component";
import { RegistrationEditComponent } from "./components/pages/preview2/components/registration-edit.component";
import { TranslationsEditComponent } from "./components/pages/preview2/components/translations-edit.component";
import { PreviewFullComponent } from "./components/pages/preview2/preview-full.component";
import { Preview2Component } from "./components/pages/preview2/preview2.component";
import {
  RegistrationActivityConditionComponent,
} from "./components/pages/registrations/components/activity-condition/activity-condition-registration.component";
import {
  ActivityConditionEditComponent,
} from "./components/pages/registrations/components/activity-condition/section/activity-condition-edit.component";
import {
  ActivityConditionListComponent,
} from "./components/pages/registrations/components/activity-condition/section/activity-condition-list.component";
import {
  ServiceLocationEditComponent,
} from "./components/pages/registrations/components/collection-place/section/service-location-edit.component";
import {
  ServiceLocationListComponent,
} from "./components/pages/registrations/components/collection-place/section/service-location-list.component";
import {
  RegistrationServiceLocationComponent,
} from "./components/pages/registrations/components/collection-place/service-location-registration.component";
import { RegistrationCostComponent } from "./components/pages/registrations/components/cost/cost-registration.component";
// cost modal
import {
  CostDeterminantSection,
} from "./components/pages/registrations/components/cost/section/cost-determinant-section.component";
import { CostEditComponent } from "./components/pages/registrations/components/cost/section/cost-edit.component";
import { CostItemsComponent } from "./components/pages/registrations/components/cost/section/cost-items.component";
import {
  RegistrationDescriptionComponent,
} from "./components/pages/registrations/components/description/description-registration.component";
import {
  DeterminantNewComponent,
} from "./components/pages/registrations/components/determinant/determinant-new.component";
import {
  RegistrationDeterminantComponent,
} from "./components/pages/registrations/components/determinant/determinant-registration.component";
import {
  BooleanDeterminantComponent,
} from "./components/pages/registrations/components/determinant/types/boolean-determinant.component";
import {
  CatalogDeterminantComponent,
} from "./components/pages/registrations/components/determinant/types/catalog-determinant.component";
import {
  DateDeterminantComponent,
} from "./components/pages/registrations/components/determinant/types/date-determinant.component";
import {
  MoneyDeterminantComponent,
} from "./components/pages/registrations/components/determinant/types/money-determinant.component";
import {
  NumericDeterminantComponent,
} from "./components/pages/registrations/components/determinant/types/numeric-determinant.component";
import {
  SelectDeterminantComponent,
} from "./components/pages/registrations/components/determinant/types/select-determinant.component";
import {
  SubCatalogDeterminantComponent,
} from "./components/pages/registrations/components/determinant/types/sub-catalog-determinant.component";
import {
  TextDeterminantComponent,
} from "./components/pages/registrations/components/determinant/types/text-determinant.component";
import {
  FileDeterminantComponent,
} from "./components/pages/registrations/components/determinant/types/file-determinant.component";
import {
  ValidationDeterminantComponent,
} from "./components/pages/registrations/components/determinant/types/validation-determinant.component";
import {
  ButtonDeterminantComponent,
} from "./components/pages/registrations/components/determinant/types/button-determinant.component";
import {
  FileUploadDeterminantComponent,
} from "./components/pages/registrations/components/determinant/types/file-upload-determinant.component";
import {
  GridComponentDeterminantComponent,
} from "./components/pages/registrations/components/determinant/types/grid-component-determinant.component";
import { InstitutionComponent } from "./components/pages/registrations/components/institution/institution.component";
import {
  InstitutionEditComponent,
} from "./components/pages/registrations/components/institution/section/institution-edit.component";
import { RegistrationLawComponent } from "./components/pages/registrations/components/law/law-registration.component";
import { LawEditComponent } from "./components/pages/registrations/components/law/section/law-edit.component";
import { LawListComponent } from "./components/pages/registrations/components/law/section/law-list.component";
import {
  RegistrationRegionComponent,
} from "./components/pages/registrations/components/region/region-registration.component";
import {
  RegistrationRequirementComponent,
} from "./components/pages/registrations/components/requirement/requirement-registration.component";
import {
  RequirementEditComponent,
} from "./components/pages/registrations/components/requirement/section/requirement-edit.component";
// Requirement modal
import {
  RequirementListComponent,
} from "./components/pages/registrations/components/requirement/section/requirement-list.component";
import {
  RegistrationResultComponent,
} from "./components/pages/registrations/components/result/result-registration.component";
import { ResultEditComponent } from "./components/pages/registrations/components/result/section/result-edit.component";
import { ResultListComponent } from "./components/pages/registrations/components/result/section/result-list.component";
import { UnitEditComponent } from "./components/pages/registrations/components/unit/section/unit-edit.component";
import { UnitComponent } from "./components/pages/registrations/components/unit/unit.component";
import { NewRegistrationComponent } from "./components/pages/registrations/new-registration.component";
import {
  RegistrationDetailComponent,
} from "./components/pages/registrations/registration-detail/registration-detail.component";
/**********************************************************************
 -Registraion page components
 ***********************************************************************/
import { RegistrationsComponent } from "./components/pages/registrations/registrations.component";
import { BotSelectorComponent } from "./components/pages/roles/components/bot/bot-selector.component";
import { DeterminantRoleComponent } from "./components/pages/roles/components/determinant/determinant-role.component";
import { FlowComponent } from "./components/pages/roles/components/flow/flow.component";
import {
  RoleFlowDeterminantComponent,
} from "./components/pages/roles/components/flow/role-flow-determinant/role-flow-determinant.component";
import {
  RoleFlowRegistrationComponent,
} from "./components/pages/roles/components/flow/role-flow-registration/role-flow-registration.component";
import { InstitutionRoleComponent } from "./components/pages/roles/components/institution/institution-role.component";
import { RoleManageComponent } from "./components/pages/roles/components/manage/role-manage.component";
import {
  RegistrationsRoleManageComponent,
} from "./components/pages/roles/components/registrations/registrations-role-manage.component";
import {
  RegistrationsRoleComponent,
} from "./components/pages/roles/components/registrations/registrations-role.component";
import { MappingBoardComponent } from "./components/pages/roles/components/service/mapping-board/mapping-board.component";
import {
  FieldItemComponent,
} from "./components/pages/roles/components/service/mapping-board/right-side/field-item/field-item.component";
import {
  FieldItemGroupComponent,
} from "./components/pages/roles/components/service/mapping-board/right-side/field-item-group/field-item-group.component";
import {
  RightSideComponent,
} from "./components/pages/roles/components/service/mapping-board/right-side/right-side.component";
import {
  TreeBranchComponent,
} from "./components/pages/roles/components/service/mapping-board/tree/tree-branch.component";
import {
  DataTransformationsComponent,
} from "./components/pages/roles/components/service/mapping-board/data-transformations/data-transformations.component";
import {
  TransformSearchComponent,
} from "./components/pages/roles/components/service/mapping-board/data-transformations/types/transform-search.component";
import { ServiceRoleComponent } from "./components/pages/roles/components/service/service-role.component";
import { MessageEditComponent } from "./components/pages/roles/components/status/section/message-edit.component";
import {
  StatusMessageEditComponent,
} from "./components/pages/roles/components/status/section/status-message-edit.component";
import {
  StatusMessageListComponent,
} from "./components/pages/roles/components/status/section/status-message-list.component";
import { StatusNewComponent } from "./components/pages/roles/components/status/section/status-new.component";
import { StatusRoleComponent } from "./components/pages/roles/components/status/status-role.component";
import { StatusWrapperComponent } from "./components/pages/roles/components/status/status-wrapper.component";
import { UnitRoleComponent } from "./components/pages/roles/components/unit/unit-role.component";
import { NewRoleComponent } from "./components/pages/roles/new-role.component";
import { RoleDetailComponent } from "./components/pages/roles/role-detail.component";
/**********************************************************************
 -Roles page components
 ***********************************************************************/
import { RolesComponent } from "./components/pages/roles/roles.component";
/**********************************************************************
 -Bots page components
 ***********************************************************************/
import { BotDetailComponent } from "./components/pages/bots/bot-detail.component";
import { BotsComponent } from "./components/pages/bots/bots.component";
import { ActionsComponent } from "./components/pages/actions/actions.component";
import { ExternalLinksComponent } from "./components/pages/actions/external-links/external-links.component";
import { BotManageComponent } from "./components/pages/bots/components/manage/bot-manage.component";
import { RoleBotManageComponent } from "./components/pages/bots/components/manage/role-bot-manage.component";
import { BotTriggersComponent } from "./components/pages/bots/components/triggers/bot-triggers.component";
import { EditBotComponent } from "./components/pages/bots/edit-bot/edit-bot.component";
import { NewBotComponent } from "./components/pages/bots/new-bot.component";
/**********************************************************************
 -Service page components
 ***********************************************************************/
import { ServiceDashboardComponent } from "./components/pages/service-dashboard/service-dashboard.component";
import { ServiceCopyModalComponent } from "./components/pages/service-detail/service-copy-modal.component";
import { ServiceDetailComponent } from "./components/pages/service-detail/service-detail.component";
import { ServiceFileUploaderComponent } from "./components/pages/service-detail/service-file-uploader.component";
import { ServiceManageComponent } from "./components/pages/service-detail/service-manage.component";
import { ServiceSelectComponent } from "./components/pages/service-select/service-select.component";
import { CurrencyComponent } from "./components/pages/settings/currency.component";
import { SettingsComponent } from "./components/pages/settings/settings.component";
import { MediaLibraryComponent } from "./components/pages/media/media-library.component";
import { MediaPickerComponent } from "./components/utils/media-picker/media-picker.component";
import { MediaApiService } from "./services/media-controller/media-api.service";
import { TimeManagementComponent } from "./components/pages/settings/time-management/time-management.component";
import { CostsTableComponent } from "./components/pages/tables/costs-table.component";
import { DeterminantsTableComponent } from "./components/pages/tables/determinants-table.component";
import { DocumentsTableComponent } from "./components/pages/tables/documents-table.component";
import { ResultsTableComponent } from "./components/pages/tables/results-table.component";
import { FieldsGeneratorComponent } from "./components/pages/tables/fields-generator.component";
import { FieldsTableComponent } from "./components/pages/tables/fields-table.component";
import { InstitutionsTableComponent } from "./components/pages/tables/institutions-table.component";
import { LawsDetailComponent } from "./components/pages/tables/law-detail.component";
import { LawNewComponent } from "./components/pages/tables/law-new.component";
import { LawsTableComponent } from "./components/pages/tables/laws-table.component";
import { MessagesTableComponent } from "./components/pages/tables/messages-table.component";
import { TranslationsComponent } from "./components/pages/translations/translations.component";
import { VariablesTabsComponent } from "./components/pages/variables-tabs/variables-tabs.component";
import { ApplicableFormioComponentsPipe } from "./components/pipes/applicable-formio-components";
import { ExpandedListFilterPipe } from "./components/pipes/expanded-list-filter-pipe";
import { CatalogFilterPipe } from "./components/pipes/catalog-filter-pipe";
import { FilterByCategoryPipe } from "./components/pipes/filter-by-category-pipe";
import { SortByPipe } from "./components/pipes/sort-by-pipe";
import { FilterOperatorsPipe } from "./components/pipes/filter-operators-pipe";
import { PrimaryTextButtonComponent } from "./components/primary-text-button/primary-text-button.component";
import { SearchInputRowComponent } from "./components/search-input-row/search-input-row.component";
import { ServiceAddComponent } from "./components/service-select/add-service/service-add.component";
import { ServiceSelectPanelComponent } from "./components/service-select/service-select-panel.component";
import { SidenavComponent } from "./components/sidenav/sidenav.component";
import { AndOrComponent } from "./components/utils/and_or.component";
import { BaseModalComponent } from "./components/utils/base_modal.component";
import { ConfirmationModalComponent } from "./components/utils/confirmation-modal.component";
import { ConfirmationComponent } from "./components/utils/confirmation.component";
import { MarkerModalComponent } from "./components/utils/marker-modal.component";
import { ModalComponent } from "./components/utils/modal/modal.component";
import { DeterminantList } from "./components/utils/query-builder/determinant-list";
import { DeterminantQueryReadOnly } from "./components/utils/query-builder/determinant-query-readonly";
import { QueryBuilder } from "./components/utils/query-builder/query-builder";
import { RegistrationSelectionComponent } from "./components/utils/registrationselection.component";
import { WorkFormContainerComponent } from "./components/work-form-container/work-form-container.component";
import { WrapperComponent } from "./components/wrapper/wrapper.component";
import { AuthGuardCAS } from "./guards/auth.guard.cas";
import { AuthGuardKeycloak } from "./guards/auth.guard.keycloak";
import { ActivityConditionControllerService } from "./services/activity-condition/activity-condition-controller.service";
import { ActivityConditionService } from "./services/activity-condition/activity-condition.service";
import {
  RegistrationActivityConditionControllerService,
} from "./services/activity-condition/registration-activity-condition-controller.service";
import { AttributesControllerService } from "./services/attributes-controller/attributes-controller.service";
/**********************************************************************
 -SERVICE IMPORTS
 **********************************************************************/
import { CasService } from "./services/auth/cas-service";
import { AuthInterceptor } from "./services/auth-interceptor";
import {
  ActivityConditionTypeControllerService,
} from "./services/classifications-controller/activity-condition-type-controller.service";
import { ClassificationControllerService } from "./services/classifications-controller/classification-controller.service";
import {
  ClassificationExternalidControllerService,
} from "./services/classifications-controller/classification-externalid-controller.service";
import {
  ClassificationGroupControllerService,
} from "./services/classifications-controller/classification-group-controller.service";
import { ClassificationService } from "./services/classifications-controller/classification.service";
import { DocumentControllerService } from "./services/classifications-controller/document-controller.service";
import { CostControllerService } from "./services/cost-controller/cost-controller.service";
import { CostService } from "./services/cost-controller/cost.service";
import { CurrencyControllerService } from "./services/currency-controller/currency-controller.service";
import { InstitutionControllerService } from "./services/institution-controller/institution-controller.service";
import {
  RegistrationInstitutionControllerService,
} from "./services/institution-controller/institution-registration-controller.service";
import { InstitutionService } from "./services/institution-controller/institution.service";
import { LanguageControllerService } from "./services/language-controller/language-controller.service";
import { LawControllerService } from "./services/laws-controller/law-controller.service";
import { LawService } from "./services/laws-controller/law.service";
import { RegistrationLawControllerService } from "./services/laws-controller/registration-law-controller.service";
import { LocationService } from "./services/location-controller/location.service";
import {
  RegistrationServiceLocationControllerService,
} from "./services/location-controller/registration-service-location-controller.service";
import { ServiceLocationControllerService } from "./services/location-controller/service-location-controller.service";
import { MessageControllerService } from "./services/message-controller/message-controller.service";
import {
  NotificationDeterminantControllerService,
} from "./services/message-controller/notification-determinant-controller.service";
import { MonitoringControllerService } from "./services/monitoring-controller/monitoring-controller.service";
import { MuleControllerService } from "./services/mule-controller/mule-controller.service";
import { PrintDocumentService } from "./services/print-document/print-document.service";
import { DeterminantControllerService } from "./services/registration-controller/determinant-controller.service";
import { DeterminantService } from "./services/registration-controller/determinant.service";
import { RegistrationControllerService } from "./services/registration-controller/registration-controller.service";
import { RegistrationService } from "./services/registration-controller/registration.service";
import {
  DocumentRequirementControllerService,
} from "./services/requirement-controller/document-requirement-controller.service";
import { RequirementControllerService } from "./services/requirement-controller/requirement-controller.service";
import { RequirementService } from "./services/requirement-controller/requirement.service";
import { DocumentResultControllerService } from "./services/result-controller/document-result-controller.service";
import { NewDeterminantHelperService } from "./services/result-controller/new-determinant-helper.service";
import { ResultService } from "./services/result-controller/result.service";
import { RoleInstitutionControllerService } from "./services/role-controller/role-institution-controller.service";
import { RoleStatusService } from "./services/role-controller/role-status.service";
import { RoleService } from "./services/role-controller/role.service";
import { ServiceControllerService } from "./services/service-controller/service-controller.service";
import { VersionService } from "./services/version/version.service";
import { DomainControllerService } from "./services/service-controller/domain-controller.service";
import { Store } from "./services/store.service";
import { MappingService } from "./services/mapping.service";
import { UnitControllerService } from "./services/unit-controller/unit-controller.service";
import { RegistrationUnitControllerService } from "./services/unit-controller/unit-registration-controller.service";
import { UnitService } from "./services/unit-controller/unit.service";
import { WebsocketService } from "./services/websocket.service";
import { ServiceGroupControllerService } from "./services/service-group-controller/service-group-controller.service";

import { environment } from "../environments/environment";
import {
  FormioDeterminantBuilderComponent,
} from "./components/formio-determinant-builder/formio-determinant-builder.component";
import {
  FormioDynamicValidationComponent,
} from "./components/formio-dynamic-validation/formio-dynamic-validation.component";
import {
  FormioFormulaBuilderSectionComponent,
} from "./components/formio-formula-builder-section/formio-formula-builder-section.component";
import { FormioTabsSectionComponent } from "./components/formio-tabs-section/formio-tabs-section.component";
import { FormioTranslateComponent } from "./components/formio-translate/formio-translate.component";
import { FormulaBuilderSectionComponent } from "./components/formula-builder/formula-builder-section.component";
import { ListLinksComponent } from "./components/list-links/list-links.component";
import { MyFormioComponent } from "./components/my-formio/my-formio.component";
import { OwnershipComponent } from "./components/ownership-select/ownership.component";
import {
  ApplicantFormComponent,
} from "./components/pages/application-file/components/applicant-form/applicant-form.component";
import {
  FieldSetDefaultDateComponent,
} from "./components/pages/application-file/components/form/field-options/components/field-set-default-date.component";
import {
  RelatedDeterminantSetComponent,
} from "./components/pages/application-file/components/form/shared-options/related-determinant-set.component";
import {
  ReusableSetComponent,
} from "./components/pages/application-file/components/form/shared-options/reusable-set.component";
import { GuideFormComponent } from "./components/pages/application-file/components/guide-form/guide-form.component";
import { PaymentComponent } from "./components/pages/application-file/components/payment-form/payment.component";
import {
  CrossTablesBaseComponent,
} from "./components/pages/crossed-tables/cross-tables-base/cross-tables-base.component";
import {
  ServiceLocationRegistrationsComponent,
} from "./components/pages/crossed-tables/service-location-registrations.component";
import { PrintDocumentDetailComponent } from "./components/pages/print-documents/print-document-detail.component";
import { PrintDocumentPdfSettingsComponent } from "./components/pages/print-documents/print-document-pdf-settings.component";
import { MicroPublishModalComponent } from "./components/pages/print-documents/micro-publish-modal.component";
import { PrintDocumentsComponent } from "./components/pages/print-documents/print-documents";
import { TemplatesComponent } from "./components/pages/templates/templates.component";
import {
  BotDeterminantComponent,
} from "./components/pages/registrations/components/determinant/bot/bot-determinant.component";
import {
  RegistrationLocationSelectionComponent,
} from "./components/pages/registrations/components/determinant/types/select-collection-place.component";
import { RoleBotSelectorComponent } from "./components/pages/roles/components/bot/role-bot-selector.component";
import { SwaggerUrlComponent } from "./components/pages/settings/swagger-url.component";
import { EditFormFieldComponent } from "./components/pages/tables/edit-form-field.component";
import { FieldTreeNode } from "./components/pages/tables/field-tree-node.component";
import { ServiceLocationTableComponent } from "./components/pages/tables/service-location-table.component";
import { TablesBaseComponent } from "./components/pages/tables/tables-base/tables-base.component";
import { SearchSelectComponent } from "./components/search-select/search-select.component";
import { InstancesComponent } from "./components/pages/settings/instances.component";
import { GroupManageComponent } from "./components/pages/settings/group-manage.component";
import { GroupsComponent } from "./components/pages/settings/groups.component";
import { UserLevelComponent } from "./components/user-level/user-level.component";
import { BotBuilder } from "./components/utils/bot-builder/bot-builder";
import { BotListComponent } from "./components/utils/bot-builder/bot-list.component";
import { FieldTreePickerComponent } from "./components/utils/field-tree-picker/field-tree-picker.component";
import { FormActivatorComponent } from "./components/utils/form-activator/form-activator.component";
import { ObjectControllerService } from "./services/object-controller/object-controller.service";
import { OwnershipControllerService } from "./services/ownership/ownership-controller.service";
import {
  ServiceCategoryControllerService,
} from "./services/service-categories-controller/service-category-controller.service";
import { ServiceCopyControllerService } from "./services/service-controller/service-copy-controller.service";
import { SwaggerControllerService } from "./services/swagger-url-controller/swagger-url-controller.service";
import { FieldsGroupComponent } from "./components/pages/crossed-tables/fields-group/fields-group.component";
import { ComponentFormulasComponent } from "./components/pages/formulas/component-formulas.component";
import { DynamicValidationComponent } from "./components/pages/validation/dynamic/dynamic-validation.component";
import { ValidationBlacklistComponent } from "./components/pages/validation/blacklist/blacklist.component";
import { TabsComponent } from "./components/pages/tabs/tabs.component";
import { ComponentValidationControllerService } from "./services/form-controller/component-validation-controller.service";
import { ComponentFormulaControllerService } from "./services/form-controller/component-formula-controller.service";
import { ComponentTabControllerService } from "./services/form-controller/component-tab-controller.service";
import { ExternalServerControllerService } from "./services/service-controller/external-server-controller.service";
import { DatabaseItemComponent } from "./components/pages/service-detail/database-item/database-item.component";
import {
  TransformManyToOneComponent,
} from "./components/pages/roles/components/service/mapping-board/data-transformations/types/transform-many-to-one.component";
import {
  TransformInputComponent,
} from "./components/pages/roles/components/service/mapping-board/data-transformations/types/transform-input.component";
import { DocumentFormComponent } from "./components/pages/application-file/components/document-form/document.component";
import {
  ServiceConfigurationComponent,
} from "./components/pages/service-detail/service-configuration/service-configuration.component";
import { ServiceOptionsComponent } from "./components/pages/service-detail/service-options/service-options.component";
import {
  ServicePublishHistoryComponent,
} from "./components/pages/service-detail/service-publish-history/service-publish-history.component";
import { ServiceExportComponent } from "./components/pages/service-detail/service-export/service-export.component";
import {
  ServiceDatabasesComponent,
} from "./components/pages/service-detail/service-databases/service-databases.component";
import {
  ToggleFormSidebarComponent,
} from "./components/pages/application-file/components/toggle-form-sidebar/toggle-form-sidebar.component";
import { HistoryFormComponent } from "./components/pages/application-file/components/history-form/history-form.component";
import { FormfieldDeterminantsComponent } from "./components/pages/crossed-tables/formfield-determinants.component";
import {
  FieldsGroupDeterminantComponent,
} from "./components/pages/crossed-tables/fields-group-determinant/fields-group-determinant.component";
import {
  FieldsDeterminantEditComponent,
} from "./components/pages/crossed-tables/fields-determinant-edit/fields-determinant-edit.component";
import { SystemPropertiesControllerService } from "./services/system-settings/system-properties-controller.service";
import { SearchServicesComponent } from "./components/search-services/search-services.component";
import {
  FieldsGroupPropertyComponent,
} from "./components/pages/crossed-tables/fields-group-property/fields-group-property.component";
import {
  NewPrintDocumentsComponent,
} from "./components/pages/print-documents/new-print-documents/new-print-documents.component";
import { AuthService } from "./services/auth/auth-service";
import {
  ServiceImportModalComponent,
} from "./components/pages/service-detail/service-export/service-import-modal.component";
import { DrawerComponent } from "./components/utils/drawer/drawer.component";
import { NgxPaginationModule } from "ngx-pagination";
import {
  TransformFieldOperatorComponent,
} from "./components/pages/roles/components/service/mapping-board/data-transformations/types/transform-field-operator.component";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
  GeneralDeterminantBuilderComponent,
} from "./components/general-determinant-builder/general-determinant-builder.component";
import { ActionBuilderComponent } from "./components/utils/action-builder/action-builder.component";
import { DeterminantInstitutionComponent } from "./components/pages/roles/components/determinant/determinant-institution/determinant-institution.component";
import {
  DeterminantRoleInstitutionComponent,
} from "./components/pages/roles/components/determinant/determinant-role-institution/determinant-role-institution.component";
import { ConfirmationPopover } from "./components/confirmation-popover/confirmation-popover.component";
import { ServiceDebugComponent } from "./components/pages/service-detail/service-debug/service-debug.component";
import { DeterminantTriggersComponent } from "./components/determinant-triggers/determinant-triggers.component";
import { ServiceDocumentationComponent } from "./components/pages/service-documentation/service-documentation.component";
import { SflComponent } from "./components/utils/sfl/sfl.component";
import { SflItemComponent } from "./components/utils/sfl/sfl-item/sfl-item.component";
import { FieldTableComponent } from "./components/pages/tables/fields-table/field-table/field-table.component";
import { FormFieldDeterminantComponent } from "./components/pages/registrations/components/determinant/types/formfield-determinant.component";
import { GridValidationComponent } from "./components/pages/grid-validation/grid-validation.component";
import { FormControllerService } from "./services/form-controller/form-controller.service";
import StyleService from "./services/style-service/style-service";
import { CvfMappingComponent } from "./components/pages/bots/components/cvf-mapping/cvf-mapping.component";
import { GenericSflComponent } from "./components/pages/bots/components/cvf-mapping/generic-sfl/generic-sfl.component";
import { GenericSflItemComponent } from "./components/pages/bots/components/cvf-mapping/generic-sfl/generic-sfl-item/generic-sfl-item.component";
import { TransformValueTypeComponent } from "./components/pages/roles/components/service/mapping-board/data-transformations/types/transform-value-type.component";
import { TransformValueInputComponent } from "./components/pages/roles/components/service/mapping-board/data-transformations/types/transform-value-input.component";
import { TransformGridComponent } from "./components/pages/roles/components/service/mapping-board/data-transformations/types/transform-grid.component";
import { ManyToOneHandlerComponent } from "./components/pages/roles/components/service/mapping-board/data-transformations/many-to-one-handler/many-to-one-handler.component";
import { TransformGridGroupbyComponent } from "./components/pages/roles/components/service/mapping-board/data-transformations/types/transform-grid-groupby.component";
import { OneToManyQueryComponent } from "./components/pages/roles/components/service/mapping-board/data-transformations/one-to-many-query/one-to-many-query.component";
import { EntriesFiltersComponent } from "./components/pages/roles/components/service/mapping-board/data-transformations/entries-filters/entries-filters.component";
import { ServiceSettingsConfigurationComponent } from "./components/service-select/service-settings/settings/service-settings-configuration/service-settings-configuration.component";
import { ServiceSettingsOptionsComponent } from "./components/service-select/service-settings/settings/service-settings-options/service-settings-options.component";
import { ToggleButtonsComponent } from "./components/utils/toggle-buttons/toggle-buttons.component";
import { ToggleSwitchComponent } from "./components/utils/toggle-switch/toggle-switch.component";
import { DeterminantTriggersFieldComponent } from "./components/determinant-triggers/determinant-triggers-field/determinant-triggers-field.component";
import { DeterminantTriggersFieldContentComponent } from "./components/determinant-triggers/determinant-triggers-field-content/determinant-triggers-field-content.component";
import { DeterminantTriggersActionComponent } from "./components/determinant-triggers/determinant-triggers-action/determinant-triggers-action.component";
import { DeterminantManagerComponent } from "./components/determinant-manager/determinant-manager.component";
import { TimesPopoverComponent } from "./components/times-popover/times-popover.component";
import { WarningTooltipComponent } from "./components/utils/warning-tooltip/warning-tooltip.component";
import { InlineLabelEditComponent } from "./components/utils/inline-label-edit/inline-label-edit.component";
import { MappingCheckerComponent } from "./components/pages/roles/components/service/mapping-board/mapping-checker/mapping-checker.component";
import {
  PreviewTranslationsControllerService
} from "./services/preview-translations-controller/preview-translations-controller.service";
import { ServiceSettingsDebugComponent } from "./components/service-select/service-settings/settings/service-settings-debug/service-settings-debug.component";
import { DryRunComponent } from "./components/utils/dry-run/dry-run.component";
import { ServiceSettingsExportComponent } from "./components/service-select/service-settings/settings/service-settings-export/service-settings-export.component";
import { ServiceSettingsDatabasesComponent } from "./components/service-select/service-settings/settings/service-settings-databases/service-settings-databases.component";
import { ServiceSettingsHistoryComponent } from "./components/service-select/service-settings/settings/service-settings-history/service-settings-history.component";
import { ReleaseNotesCreateComponent } from "./components/release-notes/release-notes-create/release-notes-create.component";
import { ReleaseNoteTypesHandlerComponent } from "./components/release-notes/release-note-types-handler/release-note-types-handler.component";
import { ReleaseNotesListComponent } from "./components/release-notes/release-notes-list/release-notes-list.component";
import { UsagesComponent } from "./components/utils/usages/usages.component";
import { DbUpdateComponent } from "./components/pages/bots/components/manage/db-update/db-update.component";
import { BusinessKeyComponent } from "./components/utils/business-key/business-key.component";
import { DeterminantPopoverComponent } from "./components/utils/determinant-popover/determinant-popover.component";
import { DrawerItemsComponent } from "./components/utils/drawer-items/drawer-items.component";
import { FileHandlerComponent } from "./components/utils/file-handler/file-handler.component";

export class CustomLoader implements TranslateLoader {
  constructor(private http: HttpClient) {
  }

  public getTranslation(locale: string): Observable<any> {
    return this.http.get<any>(environment.BASE_URL + "translation/cached?lang=" + locale)
      .pipe(map((translations) => translations));
  }
}

let AuthGuard;
const authProviders = [];
const authImports = [];

if (Boolean(environment.KEYCLOAK_ACTIVE)) {
  const initializeKeycloak = (keycloak: KeycloakService) => {
    return () =>
      keycloak.init({
        config: {
          url: environment.KEYCLOAK_URL,
          realm: environment.KEYCLOAK_REALM,
          clientId: environment.OAUTH_CLIENT_ID,
        },
        initOptions: {
          checkLoginIframe: true,
          checkLoginIframeInterval: 25,
          onLoad: "check-sso",
          enableLogging: true,
          silentCheckSsoRedirectUri:
            window.location.origin + "/assets/silent-check-sso.html",
        },
        bearerExcludedUrls: ["/assets", "/login", "/clients/public", "/bparest/bpa/v2016/06/translation/cached",
          "/bpa/v2016/06/translation/cached"],
        bearerPrefix: "Bearer",
      });
  };

  AuthGuard = AuthGuardKeycloak;
  authProviders.push({
    provide: APP_INITIALIZER,
    useFactory: initializeKeycloak,
    multi: true,
    deps: [KeycloakService],
  });
  authImports.push(KeycloakAngularModule);
} else {
  AuthGuard = AuthGuardCAS;
  authProviders.push(AuthGuard);
}

const providers = authProviders.concat([
  Title,
  MediaApiService,
  MonitoringControllerService,
  CostService,
  CostControllerService,
  CurrencyControllerService,
  DocumentControllerService,
  ResultService,
  RequirementService,
  DocumentResultControllerService,
  NewDeterminantHelperService,
  DocumentRequirementControllerService,
  RegistrationService,
  RegistrationControllerService,
  DeterminantService,
  Store,
  MappingService,
  ActivityConditionControllerService,
  RegistrationActivityConditionControllerService,
  ServiceLocationControllerService,
  RegistrationServiceLocationControllerService,
  LawService,
  LawControllerService,
  RegistrationLawControllerService,
  DeterminantControllerService,
  RequirementControllerService,
  ServiceControllerService,
  DomainControllerService,
  ExternalServerControllerService,
  ServiceCategoryControllerService,
  SystemPropertiesControllerService,
  AttributesControllerService,
  ServiceCopyControllerService,
  MuleControllerService,
  LanguageControllerService,
  SwaggerControllerService,
  ClassificationService,
  ClassificationControllerService,
  ClassificationGroupControllerService,
  ClassificationExternalidControllerService,
  ActivityConditionTypeControllerService,
  ActivityConditionService,
  LocationService,
  NotificationDeterminantControllerService,
  CasService,
  InstitutionService,
  InstitutionControllerService,
  UnitService,
  UnitControllerService,
  ObjectControllerService,
  OwnershipControllerService,
  RegistrationUnitControllerService,
  RegistrationInstitutionControllerService,
  MessageControllerService,
  RoleService,
  PrintDocumentService,
  RoleInstitutionControllerService,
  RoleStatusService,
  ComponentValidationControllerService,
  ComponentFormulaControllerService,
  ComponentTabControllerService,
  WebsocketService,
  AuthService,
  FormControllerService,
  PreviewTranslationsControllerService,
  StyleService,
  ServiceGroupControllerService,
  VersionService,
  {
    provide: APP_INITIALIZER,
    useFactory: (versionService: VersionService) => () => versionService.load(),
    deps: [VersionService],
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthInterceptor,
    multi: true,
  },
]);

@NgModule({
  imports: [
    authImports.concat([
      BrowserModule,
      LoadingBarHttpClientModule,
      BrowserAnimationsModule,
      CKEditorModule,
      AgmCoreModule.forRoot({
        apiKey: "AIzaSyB3KrEtdT5awfRTeFw-_bdu4xjfrzXkxN4",
      }),
      AgmJsMarkerClustererModule,
      FilterPipeModule,
      FormioModule,
      FormsModule,
      CustomFormsModule,
      HttpClientModule,
      NgSelectModule,
      NgxUploaderModule,
      UiSwitchModule,
      DragDropModule,
      ReactiveFormsModule,
      ResizableModule,
      PostMessageModule,
      BaseModalComponent,
      LoadingBarHttpClientModule,
      NgxDatatableModule,
      NgxPaginationModule,
      ToastrModule.forRoot(),
      BsDropdownModule.forRoot(),
      DatepickerModule.forRoot(),
      CollapseModule.forRoot(),
      ModalModule.forRoot(),
      AlertModule.forRoot(),
      TabsModule.forRoot(),
      TooltipModule.forRoot(),
      ButtonsModule.forRoot(),
      PopoverModule.forRoot(),
      DndModule,
      TagInputModule,
      MatFormFieldModule,
      MatListModule,
      MatInputModule,
      MatAutocompleteModule,
      MatSelectModule,
      AngularResizedEventModule,
      TranslateModule.forRoot({
        loader: { provide: TranslateLoader, useClass: CustomLoader, deps: [HttpClient] },
      }),
      RouterModule.forRoot([
        {
          path: "login",
          component: LoginComponent,
        },
        {
          path: "settings",
          component: SettingsComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "settings/media",
          component: MediaLibraryComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "catalogues",
          component: ClassificationComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "",
          component: ServiceSelectComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services",
          component: ServiceSelectComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/archived",
          component: ServiceSelectComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId",
          component: ServiceDetailComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/documentation",
          component: ServiceDocumentationComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/settings",
          component: ServiceManageComponent,
          canActivate: [AuthGuard],
          children: [
            {
              path: "",
              redirectTo: "configuration",
              pathMatch: "full",
            },
            {
              path: "configuration",
              component: ServiceConfigurationComponent,
            },
            {
              path: "options",
              component: ServiceOptionsComponent,
            },
            {
              path: "history",
              component: ServicePublishHistoryComponent,
            },
            {
              path: "export",
              component: ServiceExportComponent,
            },
            {
              path: "databases",
              component: ServiceDatabasesComponent,
            },
            {
              path: "debug",
              component: ServiceDebugComponent,
            },
          ],
        },
        {
          path: "services/:serviceId/preview2",
          component: Preview2Component,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/previewfull",
          component: PreviewFullComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/formiodeterminant",
          component: FormioDeterminantBuilderComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/newFormiodeterminant",
          component: DeterminantTriggersComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/generaldeterminant",
          component: GeneralDeterminantBuilderComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/grid-validation",
          component: GridValidationComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/formiovalidation/:subTab",
          component: FormioDynamicValidationComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/botsdeterminant",
          component: BotDeterminantComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/botstriggers",
          component: BotTriggersComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/formioformula",
          component: FormioFormulaBuilderSectionComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/formiotabs",
          component: FormioTabsSectionComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/formiotranslate",
          component: FormioTranslateComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/registrations",
          component: RegistrationsComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/registrations/new",
          component: NewRegistrationComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/registrations/:registrationId",
          component: RegistrationDetailComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/guides",
          component: ApplicationFileFormsContainer,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/guides/:guideId",
          component: ApplicationFileFormsContainer,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/notification",
          component: NotificationComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/roles",
          component: RolesComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/roles/new",
          component: NewRoleComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/roles/:roleId",
          component: RoleDetailComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/bots/:botId",
          component: BotDetailComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/bots",
          redirectTo: "services/:serviceId/actions/bots",
          pathMatch: "full",
        },
        {
          path: "services/:serviceId/actions",
          redirectTo: "services/:serviceId/actions/bots",
          pathMatch: "full",
        },
        {
          path: "services/:serviceId/actions/:tab",
          component: ActionsComponent,
          canActivate: [AuthGuard],
        }, {
          path: "services/:serviceId/bots/new",
          component: NewBotComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/determinantstable",
          component: DeterminantsTableComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/variables/tables",
          component: TablesBaseComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/variables/cross-tables",
          component: CrossTablesBaseComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/variables/instance-catalogs",
          component: ClassificationComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "variables/instance-catalogs",
          component: ClassificationComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/variables/instance-catalogs/:catalogueId",
          component: ClassificationComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "variables/instance-catalogs/:catalogueId",
          component: ClassificationComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/documentstable",
          component: DocumentsTableComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/resultstable",
          component: ResultsTableComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/coststable",
          component: CostsTableComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/institutionstable",
          component: InstitutionsTableComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/card",
          component: CardPageComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/forms/:formId",
          component: ApplicationFileFormsContainer,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/templates",
          component: TemplatesComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/templates/:tab",
          component: TemplatesComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/print-documents",
          component: TemplatesComponent,
          data: { defaultTab: "certificates" },
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/print-documents/:documentId",
          component: PrintDocumentDetailComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/print-documents/new",
          component: PrintDocumentDetailComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/print-documents/new/:templateId",
          component: PrintDocumentDetailComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/fieldstable",
          component: FieldsTableComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/messagestable",
          component: TemplatesComponent,
          data: { defaultTab: "messages" },
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/lawstable",
          component: LawsTableComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/lawstable/:lawId",
          component: LawsDetailComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/lawstable/new",
          component: LawsDetailComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/locationstable",
          component: ServiceLocationTableComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/fieldgeneratortable",
          component: FieldsGeneratorComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/docsreg",
          component: DocumentRegistrationsComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/detreg",
          component: DeterminantRegistrationsComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/costreg",
          component: CostRegistrationsComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/instreg",
          component: InstitutionRegistrationsComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/rolereg",
          component: RoleRegistrationsComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/formfieldsreg",
          component: FormFieldRegistrationsComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/formfieldsdet",
          component: FormfieldDeterminantsComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/fieldsProps",
          component: FieldPropertiesComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/servicelocreg",
          component: ServiceLocationRegistrationsComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "services/:serviceId/pdf-export",
          component: PdfExportComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "translate/:serviceId",
          component: TranslationsComponent,
          canActivate: [AuthGuard],
        },
        {
          path: "translate",
          component: TranslationsComponent,
          canActivate: [AuthGuard],
        },
        {
          // Wildcard — matches any URL not claimed by a route above.
          // Renders the shared empty-state so typos/stale links don't
          // leave users on a blank screen.
          path: "**",
          component: PageNotFoundComponent,
        },
      ]),
    ]),
    PopoverModule,
    FormsModule,
    CustomFormsModule,
    TooltipModule,
    TranslateModule,
    BsDatepickerModule.forRoot(),
  ],
  declarations: [
    AppComponent,
    ApplicantFormComponent,
    GuideFormComponent,
    PrintDocumentsComponent,
    PrintDocumentDetailComponent,
    PrintDocumentPdfSettingsComponent,
    MicroPublishModalComponent,
    TemplatesComponent,
    HeaderComponent,
    AndOrComponent,
    ConfirmationComponent,
    ConfirmationModalComponent,
    ServiceCopyModalComponent,
    ServiceImportModalComponent,
    MarkerModalComponent,
    ModalComponent,
    LoginComponent,
    LanguageComponent,
    SettingsComponent,
    MediaLibraryComponent,
    MediaPickerComponent,
    CurrencyComponent,
    SwaggerUrlComponent,
    ClassificationComponent,
    OtherClassificationComponent,
    ClassificationRegistrationComponent,
    ClassificationTypeNewModalComponent,
    ActivityConditionTypeComponent,
    TranslationsComponent,
    RelatedDeterminantSetComponent,
    ReusableSetComponent,
    ServiceSelectComponent,
    SidenavComponent,
    ServiceDetailComponent,
    ServiceManageComponent,
    ServiceDashboardComponent,
    FileUploaderComponent,
    ServiceFileUploaderComponent,
    RegistrationsComponent,
    CollapsiblePaneComponent,
    LawsTableComponent,
    LawsDetailComponent,
    LawNewComponent,
    CardPageComponent,
    WrapperComponent,
    ServiceSelectPanelComponent,
    ServiceAddComponent,
    TabMenu,
    DocumentEditComponent,
    ListGridComponent,
    MyFormioComponent,
    NewRegistrationComponent,
    RegistrationDetailComponent,
    RegistrationDescriptionComponent,
    RegistrationDeterminantComponent,
    RegistrationRegionComponent,
    RegistrationRequirementComponent,
    InstitutionComponent,
    UnitComponent,
    UnitEditComponent,
    InstitutionEditComponent,
    InstitutionRoleComponent,
    RegistrationResultComponent,
    RegistrationCostComponent,
    DeterminantNewComponent,
    FormFieldDeterminantComponent,
    RequirementListComponent,
    RequirementEditComponent,
    ResultListComponent,
    ResultEditComponent,
    SendFileComponent,
    DocumentFormComponent,
    PaymentComponent,
    StatusMessageListComponent,
    StatusNewComponent,
    StatusMessageEditComponent,
    MessageEditComponent,
    CostEditComponent,
    CostItemsComponent,
    CostDeterminantSection,
    RegistrationActivityConditionComponent,
    RegistrationServiceLocationComponent,
    ServiceLocationEditComponent,
    ActivityConditionListComponent,
    ServiceLocationListComponent,
    ActivityConditionEditComponent,
    RegistrationLawComponent,
    LawListComponent,
    LawEditComponent,
    FileDeterminantComponent,
    BooleanDeterminantComponent,
    DateDeterminantComponent,
    MoneyDeterminantComponent,
    NumericDeterminantComponent,
    TextDeterminantComponent,
    SelectDeterminantComponent,
    ValidationDeterminantComponent,
    ButtonDeterminantComponent,
    FileUploadDeterminantComponent,
    GridComponentDeterminantComponent,
    CatalogDeterminantComponent,
    SubCatalogDeterminantComponent,
    EntityNotFoundComponent,
    PageNotFoundComponent,
    IconComponent,
    OwnershipComponent,
    OwnershipSelectComponent,
    CancelaryTextButtonComponent,
    PrimaryTextButtonComponent,
    LayerHeaderComponent,
    TutorialLayerComponent,
    ImportUploaderComponent,
    FormBuilderActionsComponent,
    FormBugsDrawerComponent,
    DeterminantsTableComponent,
    DocumentsTableComponent,
    ResultsTableComponent,
    CostsTableComponent,
    ServiceLocationTableComponent,
    InstitutionsTableComponent,
    FieldsTableComponent,
    EditFormFieldComponent,
    FieldsGeneratorComponent,
    FieldTreeNode,
    MessagesTableComponent,
    DocumentRegistrationsComponent,
    FieldPropertiesComponent,
    PdfExportComponent,
    RolesComponent,
    ActionsComponent,
    ExternalLinksComponent,
    BotsComponent,
    NewBotComponent,
    EditBotComponent,
    BotDetailComponent,
    BotManageComponent,
    RoleBotManageComponent,
    BotTriggersComponent,
    FieldTreePickerComponent,
    FlowComponent,
    RoleFlowDeterminantComponent,
    RoleFlowRegistrationComponent,
    RegistrationEditComponent,
    ApplicationFileTabsComponent,
    FormDescriptionComponent,
    DeterminantFormComponent,
    ApplicationFileFormsContainer,
    NotificationComponent,
    DescriptionNotificationComponent,
    NewRoleComponent,
    RoleDetailComponent,
    RoleManageComponent,
    StatusRoleComponent,
    StatusWrapperComponent,
    RegistrationsRoleComponent,
    RegistrationsRoleManageComponent,
    InstitutionRoleComponent,
    UnitRoleComponent,
    DeterminantRoleComponent,
    ServiceRoleComponent,
    BotSelectorComponent,
    RoleBotSelectorComponent,
    TreeBranchComponent,
    DataTransformationsComponent,
    TransformSearchComponent,
    RightSideComponent,
    MappingBoardComponent,
    FieldItemComponent,
    FieldItemGroupComponent,
    FieldOptionsComponent,
    FieldOptionsComponent,
    FieldSetAbbreviationComponent,
    FieldSetKeyComponent,
    FieldSetLabelComponent,
    FieldSetHiddenComponent,
    FieldSetShortLabelComponent,
    FieldSetGridOrderNrComponent,
    FieldSetGuideClassificationComponent,
    FieldSetFieldtypeComponent,
    FieldSetMaxActivitiesComponent,
    FieldSetMinActivitiesComponent,
    FieldSetQuestiontypeComponent,
    FieldSetRequiredComponent,
    FieldSetRequiredOneTrueComponent,
    FieldSetRequiredTotalPercentageComponent,
    FieldSetSelectComponent,
    FieldSetStyleComponent,
    FormioDeterminantBuilderComponent,
    GeneralDeterminantBuilderComponent,
    FormioDynamicValidationComponent,
    BotDeterminantComponent,
    FormioFormulaBuilderSectionComponent,
    FormioTabsSectionComponent,
    FormioTranslateComponent,
    Preview2Component,
    PreviewFullComponent,
    FieldSetDisabledComponent,
    DocumentRegistrationModal,
    DeterminantRegistrationsComponent,
    CostRegistrationsComponent,
    ServiceLocationRegistrationsComponent,
    InstitutionRegistrationsComponent,
    RoleRegistrationsComponent,
    FormFieldRegistrationsComponent,
    FieldSetAddAnotherPositionComponent,
    FieldSetNotVisibleComponent,
    FieldSetNotVisibleEditModeComponent,
    FieldSetTotalVisibleInFooterComponent,
    FieldSetCalculatePercentageComponent,
    FieldSetCalculateSharesValueComponent,
    FieldSetContentComponent,
    FieldSetMyContentComponent,
    FieldSetMaskComponent,
    FieldSetMultipleComponent,
    FieldSetTimeComponent,
    FieldSetDateComponent,
    FieldSetDateLaterThanNowComponent,
    FieldSetDefaultComponent,
    FieldSetDefaultDateComponent,
    FieldSetCalculatedDefaultComponent,
    FieldSetCalculatedMaxComponent,
    FieldSetDateFormatComponent,
    FieldSetClassificationComponent,
    FieldSetTagComponent,
    FieldSetTableComponent,
    FieldSetMinComponent,
    FieldSetMinLengthComponent,
    FieldSetMaxComponent,
    FieldSetMaxLengthComponent,
    FieldSetMinRowsComponent,
    FieldSetRequiredRowsComponent,
    FieldSetOlderThanAgeComponent,
    FieldSetLaterThanComponent,
    FieldSetEarlierThanComponent,
    FieldSetRadioComponent,
    FieldSetDescriptionComponent,
    FieldSetTooltipComponent,
    TranslationsEditComponent,
    BotBuilder,
    QueryBuilder,
    RegistrationSelectionComponent,
    RegistrationLocationSelectionComponent,
    DeterminantQueryReadOnly,
    BotListComponent,
    DeterminantList,
    ListGroupItemComponent,
    SearchInputRowComponent,
    DraggableListGroupItemsComponent,
    DeterminantBuilderComponent,
    DeterminantBuilderNewComponent,
    DeterminantBuilderSectionComponent,
    WorkFormContainerComponent,
    ApplicableFormioComponentsPipe,
    ExpandedListFilterPipe,
    CatalogFilterPipe,
    FilterByCategoryPipe,
    SortByPipe,
    FilterOperatorsPipe,
    FormulaBuilderSectionComponent,
    InfixFormulaBuilderComponent,
    UserLevelComponent,
    FormActivatorComponent,
    VariablesTabsComponent,
    TablesBaseComponent,
    ListLinksComponent,
    CrossTablesBaseComponent,
    SearchSelectComponent,
    DocumentResultEditComponent,
    InstancesComponent,
    GroupManageComponent,
    GroupsComponent,
    FieldsGroupComponent,
    ComponentFormulasComponent,
    DynamicValidationComponent,
    ValidationBlacklistComponent,
    TabsComponent,
    DatabaseItemComponent,
    TransformManyToOneComponent,
    TransformInputComponent,
    ServiceConfigurationComponent,
    ServiceOptionsComponent,
    ServicePublishHistoryComponent,
    ServiceExportComponent,
    ServiceDatabasesComponent,
    ToggleFormSidebarComponent,
    HistoryFormComponent,
    FormfieldDeterminantsComponent,
    FieldsGroupDeterminantComponent,
    FieldsDeterminantEditComponent,
    SearchServicesComponent,
    FieldsGroupPropertyComponent,
    NewPrintDocumentsComponent,
    DrawerComponent,
    TransformFieldOperatorComponent,
    TimeManagementComponent,
    ActionBuilderComponent,
    DeterminantInstitutionComponent,
    DeterminantRoleInstitutionComponent,
    ConfirmationPopover,
    ServiceDebugComponent,
    DeterminantTriggersComponent,
    ServiceDocumentationComponent,
    SafeHtmlPipe,
    LabelFilterPipe,
    ServiceFilterPipe,
    ReplaceUnderscorePipe,
    SflComponent,
    SflItemComponent,
    FieldTableComponent,
    CvfMappingComponent,
    GenericSflComponent,
    GenericSflItemComponent,
    GridValidationComponent,
    TransformValueTypeComponent,
    TransformValueInputComponent,
    TransformGridComponent,
    ManyToOneHandlerComponent,
    TransformGridGroupbyComponent,
    OneToManyQueryComponent,
    EntriesFiltersComponent,
    ServiceSettingsConfigurationComponent,
    ServiceSettingsOptionsComponent,
    ToggleButtonsComponent,
    ToggleSwitchComponent,
    DeterminantTriggersFieldComponent,
    DeterminantTriggersFieldContentComponent,
    DeterminantTriggersActionComponent,
    DeterminantManagerComponent,
    TimesPopoverComponent,
    WarningTooltipComponent,
    InlineLabelEditComponent,
    MappingCheckerComponent,
    ServiceSettingsDebugComponent,
    DryRunComponent,
    ServiceSettingsExportComponent,
    ServiceSettingsDatabasesComponent,
    ServiceSettingsHistoryComponent,
    ReleaseNotesCreateComponent,
    ReleaseNoteTypesHandlerComponent,
    ReleaseNotesListComponent,
    UsagesComponent,
    DbUpdateComponent,
    BusinessKeyComponent,
    DeterminantPopoverComponent,
    DrawerItemsComponent,
    FileHandlerComponent,
    SourceBadgeComponent,
  ],
  exports: [
    CardPageComponent,
    WrapperComponent,
    SidenavComponent,
    ServiceDetailComponent,
    TabMenu,
    DocumentEditComponent,
    ListGridComponent,
    RegistrationsComponent,
    RegistrationDetailComponent,
    RegistrationDescriptionComponent,
    RegistrationDeterminantComponent,
    FormioDeterminantBuilderComponent,
    GeneralDeterminantBuilderComponent,
    FormioDynamicValidationComponent,
    BotDeterminantComponent,
    FormioFormulaBuilderSectionComponent,
    FormioTabsSectionComponent,
    FormioTranslateComponent,
    InstitutionComponent,
    UnitComponent,
    UnitEditComponent,
    ServiceSelectPanelComponent,
    ServiceAddComponent,
    InstitutionEditComponent,
    RegistrationRequirementComponent,
    RegistrationResultComponent,
    RegistrationCostComponent,
    ApplicationFileTabsComponent,
    FormDescriptionComponent,
    DeterminantFormComponent,
    ApplicationFileFormsContainer,
    DescriptionNotificationComponent,
    NotificationComponent,
    NewRoleComponent,
    RoleDetailComponent,
    RoleManageComponent,
    NewBotComponent,
    BotDetailComponent,
    BotManageComponent,
    RoleBotManageComponent,
    BotTriggersComponent,
    StatusRoleComponent,
    StatusWrapperComponent,
    RegistrationsRoleComponent,
    RegistrationsRoleManageComponent,
    InstitutionRoleComponent,
    UnitRoleComponent,
    ServiceRoleComponent,
    BotSelectorComponent,
    RoleBotSelectorComponent,
    TreeBranchComponent,
    DataTransformationsComponent,
    TransformSearchComponent,
    RightSideComponent,
    MappingBoardComponent,
    FieldItemComponent,
    FieldItemGroupComponent,
    ListGroupItemComponent,
    SearchInputRowComponent,
    DeterminantBuilderComponent,
    DeterminantBuilderNewComponent,
    ApplicableFormioComponentsPipe,
    ExpandedListFilterPipe,
    CatalogFilterPipe,
    FilterByCategoryPipe,
    SortByPipe,
    DeterminantBuilderSectionComponent,
    ComponentFormulasComponent,
    DynamicValidationComponent,
    ValidationBlacklistComponent,
    TabsComponent,
    TutorialLayerComponent,
    ImportUploaderComponent,
    TransformFieldOperatorComponent,
    ConfirmationPopover,
  ],
  bootstrap: [AppComponent],
  providers,
})
export class AppModule {
}
